import * as React from "react";
import { Maximize2 } from "lucide-react";
import { Button, Dialog, DialogContent, DialogDescription, DialogTitle } from "@nfp-tracker/components";
import { cn } from "@nfp-tracker/utils";
import { useElementSize } from "./useChartLayout";

export const FullscreenButton = ({ onClick, label }: { onClick: () => void; label: string }) => (
  <Button variant="outline" size="sm" className="flex-none" onClick={onClick} aria-label={label}>
    <Maximize2 className="h-4 w-4 sm:mr-2" />
    <span className="hidden sm:inline">Full screen</span>
  </Button>
);

interface ChartFullscreenDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: string;
  /** Controls and key, above the chart. Hidden on very short (phone landscape) screens. */
  toolbar?: React.ReactNode;
  /** Controls that must stay visible even on short screens. */
  controls?: React.ReactNode;
  /** Renders the chart for the measured size of the space left under the toolbar. */
  children: (size: { width: number; height: number }) => React.ReactNode;
}

/** Viewport-filling dialog for a chart; works in phone landscape (safe areas) and desktop. */
export const ChartFullscreenDialog = ({
  open,
  onOpenChange,
  title,
  description,
  toolbar,
  controls,
  children,
}: ChartFullscreenDialogProps) => {
  const [chartRef, size] = useElementSize<HTMLDivElement>();
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn(
          "flex h-[100dvh] max-h-[100dvh] w-screen max-w-none flex-col gap-2 overflow-hidden rounded-none border-0 sm:rounded-none",
          "pb-[max(0.75rem,env(safe-area-inset-bottom))] pl-[max(0.75rem,env(safe-area-inset-left))] pr-[max(0.75rem,env(safe-area-inset-right))] pt-[max(0.75rem,env(safe-area-inset-top))]"
        )}
        closeButtonClassName="right-[max(1rem,env(safe-area-inset-right))] top-[max(1rem,env(safe-area-inset-top))]"
      >
        <div className="min-w-0 pr-8">
          <DialogTitle className="text-base">{title}</DialogTitle>
          <DialogDescription className="text-xs">{description}</DialogDescription>
        </div>
        {controls}
        {/* Phone landscape: the key gives way to chart height; tooltips still explain each day. */}
        {toolbar ? <div className="[@media(max-height:520px)]:hidden">{toolbar}</div> : null}
        <div ref={chartRef} className="min-h-0 min-w-0 flex-1 overflow-y-auto">
          {open ? children(size) : null}
        </div>
      </DialogContent>
    </Dialog>
  );
};
